<template>
  <v-sheet class="rpilot-chat d-flex flex-column" :rounded="rounded">
    <AppHeader
      :show-sessions="resolvedFeatures.sessions"
      :show-skills="resolvedFeatures.skills"
      :expandable="expandable"
      :expanded="expanded"
      @toggle-expanded="toggleExpanded"
      @open-drawer="openDrawer"
    >
      <template v-if="$slots.brand" #brand>
        <slot name="brand" />
      </template>
      <template v-if="$slots['header-actions']" #header-actions>
        <slot name="header-actions" />
      </template>
    </AppHeader>

    <!-- Boot-time credit gate: when credits are enforced server-side and the
         team has no pool with balance, this replaces the chat body with a
         contact-sales takeover. Feature-off (or not gated) → renders the body
         untouched. The header stays outside so its controls keep working. -->
    <CreditGate :enabled="resolvedFeatures.credits">
      <div class="rpilot-body d-flex flex-row flex-grow-1">
        <ChatContainer
          ref="chatContainerRef"
          class="flex-grow-1"
          :features="resolvedFeatures"
          @tool-call="(payload) => emit('tool-call', payload)"
          @tool-call-cancelled="(payload) => emit('tool-call-cancelled', payload)"
          @new-chat="() => emit('new-chat')"
        >
          <template v-if="$slots['empty-state']" #empty-state>
            <slot name="empty-state" />
          </template>
        </ChatContainer>

        <SessionsDrawer
          v-model="drawerOpen"
          v-model:tab="drawerTab"
          :show-sessions="resolvedFeatures.sessions"
          :show-skills="resolvedFeatures.skills"
          :show-credits="resolvedFeatures.credits"
          :expanded="expanded"
          @request-expand="setExpanded(true)"
        />
      </div>
    </CreditGate>
  </v-sheet>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { buildInfo } from '../version.js'
import AppHeader from './layout/AppHeader.vue'
import ChatContainer from './chat/ChatContainer.vue'
import SessionsDrawer from './layout/SessionsDrawer.vue'
import CreditGate from './CreditGate.vue'
import { useBridgeStore } from '../stores/bridge.js'
import { useSessionsStore } from '../stores/sessions.js'
import { useChatStore } from '../stores/chat.js'
import { useCreditsStore } from '../stores/credits.js'
import { useRPilotConfig } from '../composables/useRPilotConfig.js'
import { useParentBridge } from '../composables/useParentBridge.js'
import { setApiConfig } from '../api/client.js'
import { registerTokenRefresher } from '../api/tokenProvider.js'

const props = defineProps({
  // Per-instance overrides for plugin-level config. When supplied, re-seed
  // the bridge store + API client so this widget can override the host's
  // global RPilot config.
  apiBaseUrl: { type: String, default: null },
  scopedToken: { type: [String, Function], default: null },
  apiUrl: { type: String, default: null },
  projectCode: { type: String, default: null },
  userId: { type: String, default: null },
  // Host-supplied context describing what the user is working on (selected
  // record, mode, etc.). Forwarded to the backend in the POST /sessions body
  // so the agent can prime its system prompt. Reactive — change it (e.g.
  // user picks a different record) and the next session will be created with
  // the new value. Re-seeding mid-session does not retroactively update the
  // backend; the host should clear the session if it wants the new context
  // applied immediately.
  sessionContext: { type: [Object, Array, String], default: null },
  features: {
    type: Object,
    default: () => ({ audio: false, images: false, files: false, thinking: true, sessions: false, skills: false, rating: true, credits: false }),
  },
  rounded: { type: [String, Boolean, Number], default: 0 },
  // Show an expand/collapse button in the header. When clicked the widget
  // flips `expanded` (v-model) and, if the parent bridge is active, fires
  // a `bridge.setModeAction` request so an iframe host can resize/relayout.
  expandable: { type: Boolean, default: false },
  expanded: { type: Boolean, default: false },
  // Vue-to-Vue hosts that mount/unmount the widget across their own views
  // (e.g. two dashboard tabs sharing one Pinia) set this so a remount
  // continues the active session instead of starting a fresh chat. If the
  // shared chat store still holds the transcript — including one that is
  // mid-stream, as happens when the agent itself triggers the host's view
  // switch from a frontend tool — the store is left untouched and rendering
  // simply resumes; otherwise the session is reloaded from the backend via
  // the id persisted in sessionStorage. "New chat" still clears as usual.
  persistSession: { type: Boolean, default: false },
})

const emit = defineEmits(['tool-call', 'tool-call-cancelled', 'new-chat', 'update:expanded'])

const cfg = useRPilotConfig()
const bridge = useBridgeStore()
const sessionsStore = useSessionsStore()
const chatStore = useChatStore()
const creditsStore = useCreditsStore()
const chatContainerRef = ref(null)
const drawerOpen = ref(false)
const drawerTab = ref('sessions')

const resolvedFeatures = computed(() => ({
  audio: props.features?.audio ?? false,
  images: props.features?.images ?? false,
  files: props.features?.files ?? false,
  thinking: props.features?.thinking ?? true,
  sessions: props.features?.sessions ?? false,
  skills: props.features?.skills ?? false,
  rating: props.features?.rating ?? true,
  credits: props.features?.credits ?? false,
}))

// Re-seed bridge store + API client whenever per-component overrides change.
// This lets a host swap auth context (e.g. project switch) without re-mounting.
watch(
  () => ({
    apiBaseUrl: props.apiBaseUrl ?? cfg.apiBaseUrl,
    scopedToken: typeof props.scopedToken === 'function' ? props.scopedToken()
      : props.scopedToken ?? (typeof cfg.scopedToken === 'function' ? cfg.scopedToken() : cfg.scopedToken),
    apiUrl: props.apiUrl ?? cfg.apiUrl,
    projectCode: props.projectCode ?? cfg.projectCode,
    userId: props.userId ?? cfg.userId,
    agentCode: cfg.sessionCreateOptions?.agentCode ?? null,
    sessionContext: props.sessionContext ?? cfg.sessionContext,
  }),
  ({ apiBaseUrl, scopedToken, apiUrl, projectCode, userId, agentCode, sessionContext }) => {
    setApiConfig({ apiBaseUrl })
    bridge.init({ scopedToken, apiUrl, projectCode, userId, agentCode, sessionContext })
  },
  { immediate: true, deep: true },
)

const parentBridge = useParentBridge()
const preserveSessionKey = `${cfg.namespace || 'rpilot'}.preserve-session`

// Keep the scoped token refreshable for long-lived embeds. A host-supplied
// getter (`scopedToken` as a function) wins; iframe deployments fall back to
// the parent bridge's refreshTokenAction. With neither, the token-bearing API
// calls behave exactly as before (no proactive refresh, no 401 retry).
const tokenGetter =
  typeof props.scopedToken === 'function' ? props.scopedToken
  : typeof cfg.scopedToken === 'function' ? cfg.scopedToken
  : null
if (tokenGetter) {
  registerTokenRefresher(() => Promise.resolve(tokenGetter()))
} else if (parentBridge.isActive && parentBridge.refreshTokenAction) {
  registerTokenRefresher(async () => {
    const res = await parentBridge.call(parentBridge.refreshTokenAction)
    return res?.token
  })
}

// Report session lifecycle to an iframe host so it can hand the id back via
// ?sessionId= on the next boot (close → reopen resumes the chat). Covers every
// mutation path — explicit create, implicit X-Session-Id adoption, drawer
// switching, new-chat clear. Fire-and-forget: a host without the handler
// rejects with unknown_action, which we ignore.
watch(() => sessionsStore.activeSessionId, (id) => {
  if (!parentBridge.isActive || !parentBridge.sessionChangedAction) return
  parentBridge.call(parentBridge.sessionChangedAction, { sessionId: id ?? null }).catch(() => {})
})

onMounted(async () => {
  if (parentBridge.isActive) parentBridge.emitReady({ ...buildInfo })

  // Pre-flight the credit gate. The host reloads the iframe on show/hide, so
  // this runs on every "show" — no visibility listener needed. CreditGate holds
  // a spinner until this resolves; on error it fails open to the chat.
  if (resolvedFeatures.value.credits) creditsStore.load()

  // A fresh mount defaults to a new chat, with two resume paths checked in
  // order. First: the one-shot preserve flag, set by the widget itself right
  // before it asks the host to reload the iframe (e.g. a sidebar↔expanded
  // relayout that recreates us) — restore from sessionStorage. Second: a
  // host-driven resume — an iframe parent passes ?sessionId= (initialSessionId
  // config) with the id it captured from our sessionChanged action when it
  // recreates a previously closed panel. Anything else — browser refresh, new
  // tab, host opening the dashboard fresh — clears the persisted id and
  // starts empty.
  let shouldRestore = false
  try {
    shouldRestore = window.sessionStorage.getItem(preserveSessionKey) === '1'
    window.sessionStorage.removeItem(preserveSessionKey)
  } catch { /* sandboxed iframe */ }

  if (shouldRestore || props.persistSession) {
    const restoredId = sessionsStore.activeSessionId
    if (restoredId && chatStore.messages.length === 0 && !chatStore.isStreaming) {
      try {
        await chatStore.loadSession(restoredId)
      } catch {
        // Persisted id no longer resolves (deleted, expired, wrong scope) —
        // drop it so we don't keep retrying on every mount.
        sessionsStore.clear()
      }
    }
    return
  }

  if (cfg.initialSessionId) {
    try {
      sessionsStore.setActive(cfg.initialSessionId)
      await chatStore.loadSession(cfg.initialSessionId)
    } catch {
      // Dead id (expired, deleted, wrong scope). Fall back to a fresh chat;
      // clearing also fires sessionChanged(null) via the watch above so the
      // host stops passing the stale id.
      chatStore.clear()
      sessionsStore.clear()
    }
    return
  }

  chatStore.clear()
  sessionsStore.clear()
})

function setExpanded(next) {
  if (next === props.expanded) return
  emit('update:expanded', next)
  // Collapsing the widget with the drawer open would leave the chat invisible;
  // close it preemptively. (Expanding leaves drawerOpen alone — the caller
  // decides whether the drawer stays up.)
  if (!next) drawerOpen.value = false
  if (parentBridge.isActive) {
    // Some hosts reload us into the other mode. Set the one-shot preserve
    // flag so that next mount restores the active session instead of starting
    // fresh; hosts that keep the iframe alive (e.g. snagr-web's persistent
    // shell) never read the flag and it's cleared by the next real mount.
    try { window.sessionStorage.setItem(preserveSessionKey, '1') } catch { /* sandboxed */ }
    // The widget's expanded/collapsed toggle maps onto the host's shell
    // modes: expanded ↔ 'dialog', collapsed ↔ 'sidebar'.
    parentBridge.call(parentBridge.setModeAction, { mode: next ? 'dialog' : 'sidebar' }).catch(() => {
      // Host can reject (or simply not handle) the action — v-model still
      // flipped so a Vue-to-Vue consumer keeps working. The unused flag will
      // be cleared by the next mount's read+remove.
    })
  }
}

function toggleExpanded() {
  setExpanded(!props.expanded)
}

function openDrawer(tab) {
  drawerTab.value = tab
  drawerOpen.value = true
}

defineExpose({
  sendMessage: (text) => chatContainerRef.value?.sendMessage(text),
  newChat: () => chatContainerRef.value?.newChat(),
  stop: () => chatContainerRef.value?.stop(),
})
</script>

<style scoped>
.rpilot-chat {
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}
.rpilot-body {
  min-height: 0;
  overflow: hidden;
  position: relative;
}
</style>
